import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { buildListingSortConfig } from './listingSortPresets';
import {
  useFilteredListingControls,
  type FilteredListingControlsSnapshot,
} from './useFilteredListingControls';
import {
  useFilteredListingQuery,
  type UseFilteredListingQueryOptions,
} from './useFilteredListingQuery';
import {
  useListingUrlState,
  type ListingUrlAdapter,
} from './useListingUrlState';

import type {
  ListingDataSource,
  ListingFetchResult,
} from './listingDataSource';
import type { ListingSortOption } from './listingToolbarConfig';
import type { TFunction } from 'i18next';

export type UseFilteredListingPageOptions<
  TRow,
  TFilters,
  TContext,
  TFilterCounts,
  TSnapshot extends FilteredListingControlsSnapshot<TFilters>,
  TResult extends ListingFetchResult<TRow, TFilterCounts> = ListingFetchResult<
    TRow,
    TFilterCounts
  >,
> = {
  urlAdapter: ListingUrlAdapter<TSnapshot>;
  defaultFilters: TFilters;
  isFiltersActive: (filters: TFilters) => boolean;
  searchPlaceholder: string;
  dataSource: ListingDataSource<TRow, TFilters, TContext, TFilterCounts>;
  context: TContext;
  enabled?: boolean;
  debounceMs?: number;
  initialSort?: string;
  initialPage?: number;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  /** When provided, exposes `sortConfig` for the action toolbar. */
  sortOptions?: (t: TFunction) => ListingSortOption[];
  onError?: UseFilteredListingQueryOptions<
    TRow,
    TFilters,
    TContext,
    TFilterCounts,
    TResult
  >['onError'];
  onSuccess?: UseFilteredListingQueryOptions<
    TRow,
    TFilters,
    TContext,
    TFilterCounts,
    TResult
  >['onSuccess'];
  /** Merged into listing snapshot for URL sync (e.g. marketplace jobTags). */
  extraSnapshot?: Partial<TSnapshot>;
  /** Called after core hydrate when URL popstate / initial hydrate runs. */
  onHydrateExtra?: (partial: Partial<TSnapshot>) => void;
};

/**
 * Composes listing controls, URL sync, and paginated query.
 * Screens should prefer this over wiring controls and query separately.
 */
export function useFilteredListingPage<
  TRow,
  TFilters,
  TContext,
  TFilterCounts,
  TSnapshot extends FilteredListingControlsSnapshot<TFilters>,
  TResult extends ListingFetchResult<TRow, TFilterCounts> = ListingFetchResult<
    TRow,
    TFilterCounts
  >,
>({
  urlAdapter,
  defaultFilters,
  isFiltersActive,
  searchPlaceholder,
  dataSource,
  context,
  enabled = true,
  debounceMs,
  initialSort,
  initialPage,
  pageSize,
  onPageSizeChange,
  sortOptions,
  onError,
  onSuccess,
  extraSnapshot,
  onHydrateExtra,
}: UseFilteredListingPageOptions<
  TRow,
  TFilters,
  TContext,
  TFilterCounts,
  TSnapshot,
  TResult
>) {
  const { t } = useTranslation();

  const controlsUrlAdapter = useMemo(
    (): ListingUrlAdapter<FilteredListingControlsSnapshot<TFilters>> => ({
      hydrateFromSearch: (search) => urlAdapter.hydrateFromSearch(search),
      buildSearch: (existing, snapshot) =>
        urlAdapter.buildSearch(existing, snapshot as TSnapshot),
    }),
    [urlAdapter],
  );

  const listing = useFilteredListingControls<TFilters>({
    defaultFilters,
    isFiltersActive,
    urlAdapter: controlsUrlAdapter,
    searchPlaceholder,
    debounceMs,
    initialSort,
    initialPage,
  });

  const listingSnapshot = useMemo(
    () =>
      ({
        ...listing.listingSnapshot,
        ...extraSnapshot,
      }) as TSnapshot,
    [listing.listingSnapshot, extraSnapshot],
  );

  const onListingHydrateOrPop = useCallback(
    (partial: Partial<TSnapshot>) => {
      listing.onListingHydrateOrPop(partial);
      onHydrateExtra?.(partial);
    },
    [listing, onHydrateExtra],
  );

  const { urlReady } = useListingUrlState(
    listingSnapshot,
    onListingHydrateOrPop,
    urlAdapter,
  );

  const query = useFilteredListingQuery<
    TRow,
    TFilters,
    TContext,
    TFilterCounts,
    TResult
  >({
    dataSource,
    snapshot: listingSnapshot,
    context,
    enabled,
    ready: urlReady,
    pageSize,
    onPageSizeChange,
    setPage: listing.setCurrentPage,
    onError,
    onSuccess,
  });

  const sortConfig = useMemo(
    () =>
      sortOptions
        ? buildListingSortConfig(
            listing.sortBy,
            listing.setSortBy,
            sortOptions(t),
          )
        : null,
    [sortOptions, listing.sortBy, listing.setSortBy, t],
  );

  return {
    ...listing,
    ...query,
    listingSnapshot,
    onListingHydrateOrPop,
    urlReady,
    sortConfig,
    urlAdapter,
  };
}
